import React from "react";
import { Box, Stack } from "@mui/material";
import { FeefoSummary } from "@/types/dto/destinations-page/destination-page-types";
import { Reviews } from "@/types/dto/homepage/homepage-types";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import FeefoSummaryGrayBar from "../common/feefo-summary-gray-bar/feefo-summary-gray-bar";
import { SectionHeader } from "../common/section-header";
import TestimonialCard from "../common/testimonial-card/testimonial-card";

export interface ReviewSectionProps {
  feefoSummary?: FeefoSummary;
  reviewsData?: Reviews;
  testId?: string;
  parentContainerType?: string;
  moduleName?: string;
  moduleRankingPosition?: string;
}

const ReviewSection = ({
  feefoSummary,
  reviewsData,
  testId = "landing-page-feefo-summary-gray-bar",
  parentContainerType = "landing-page-reviews-section",
  moduleName = "Landing Page Reviews",

  moduleRankingPosition = "6"
}: ReviewSectionProps) => {
  if (!feefoSummary && !reviewsData) return null;

  const reviewCta = reviewsData?.cta?.cta;
  const hasReviewCta = Boolean(reviewCta?.url || reviewCta?.title);
  
  const getCardDisplay = (index: number) => {
    if (index === 0) return { xs: "none", sm: "flex" };
    if (index === 1) return "flex";
    return { xs: "none", sm: "none", md: "none", lg: "flex" };
  };

  return (
    <Box
      component="section"
      className="fluid-box"
      sx={{ backgroundColor: reviewsData?.bgColor ?? "background.warm_strong" }}
      data-track="visibility"
      data-parent-container-type={parentContainerType}
      data-module-name={moduleName}
      data-testid="landing-page-reviews-section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
    >
      <Box
        className="full-width-container"
        sx={{ padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" } }}
      >
        <SectionHeader
          headerLevel="h2"
          headerText={reviewsData?.title || "100+ Years of Happy Collette Travelers"}
          headerSx={{ textAlign: "center", textTransform: "none !important" }}
          sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
          headerTestId="landing-page-reviews-section-header"
        />

        {feefoSummary && <FeefoSummaryGrayBar feefoSummary={feefoSummary} testId={testId} />}

        {reviewsData?.testimonialCards && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: "20px"
            }}
            data-testid="landing-page-reviews-grid"
          >
            {reviewsData.testimonialCards.slice(0, 3).map((card, index) => (
              <Box
                key={card.title || index}
                sx={{
                  flex: 1,
                  display: getCardDisplay(index),
                  flexDirection: "column"
                }}
                data-testid={`landing-page-reviews-column-${index}`}
              >
                <TestimonialCard
                  key={card.title || index}
                  title={card.title}
                  copy={card.copy}
                  reviewerNameSocialMediaHandle={card.reviewerNameSocialMediaHandle}
                  starRating={card.starRating}
                  ctaUrl={card?.cta?.cta?.url || undefined}
                  ctaTitle={card?.cta?.cta?.title || undefined}
                  testId={`landing-page-testimonial-card-${index}`}
                  isReadMore
                  bgImage={card.image}
                />
              </Box>
            ))}
          </Box>
        )}
        {reviewCta && hasReviewCta && (
          <Stack alignItems="center" mt="26px" data-testid="landing-page-reviews-cta-container">
            <ColletteButton
              component="a"
              href={reviewCta.url || "#"}
              variant="outlined"
              size="large"
              label={reviewCta.title || "Read All Reviews"}
              testId="landing-page-reviews-cta-button"
              data-track="click"
              sx={{
                backgroundColor: "background.default",
                textDecoration: "none"
              }}
              {...(reviewCta.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ReviewSection;
